import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ServiceFeeInfo } from "@/components/ServiceFeeInfo";
import { calculateFees, serviceFeeLabel } from "@shared/fees";
import type { CartItemWithDetails } from "@shared/schema";
import { hasListingPhoto } from "@shared/schema";
import { getListingImages } from "@shared/clothing";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { stripDemoMarker } from "@/lib/demo";
import { formatWeight, unitDisplayLabel } from "@shared/weight";

interface ShoppingCartProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItemWithDetails[];
  onUpdateQuantity: (itemId: string, qty: number) => void;
  onRemove: (itemId: string, weightUnitId?: string) => void;
  onClearCart: () => void;
  onCheckout: () => void;
}

export function ShoppingCart({
  isOpen,
  onClose,
  items,
  onUpdateQuantity,
  onRemove,
  onClearCart,
  onCheckout,
}: ShoppingCartProps) {
  const subtotal = items.reduce((sum, item) => sum + item.lineSubtotal, 0);
  const fees = calculateFees({ subtotal });
  const platformFee = fees.buyerFee;
  const total = fees.buyerTotal;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        data-testid="cart-backdrop"
      />

      {/* Cart Panel */}
      <div
        className="fixed right-0 top-0 h-full w-full md:w-96 bg-background border-l shadow-xl z-50 flex flex-col animate-in slide-in-from-right duration-200"
        style={{ paddingTop: "var(--safe-area-top, 0px)" }}
        data-testid="cart-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold" data-testid="text-cart-title">
            Shopping Cart
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-cart"
            aria-label="Close cart"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <ShoppingBag className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
              <p className="text-sm text-muted-foreground">
                Add some fresh products from local sellers to get started
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((cartItem) => {
                const price = parseFloat(cartItem.item.price);
                const unitWeight = (cartItem as any).unitWeightCentiLb as number | null | undefined;
                const unitLabel = (cartItem as any).unitLabel as string | null | undefined;
                const isWeightUnit = unitWeight != null;
                return (
                  <div
                    key={(cartItem as any).weightUnitId ?? cartItem.itemId}
                    className="flex gap-3 p-3 rounded-lg border bg-card"
                    data-testid={`cart-item-${cartItem.itemId}`}
                  >
                    {hasListingPhoto(cartItem.item) ? (
                      <img
                        src={getListingImages(cartItem.item)[0]}
                        alt={stripDemoMarker(cartItem.item.name) || cartItem.item.name}
                        loading="lazy"
                        decoding="async"
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-20 h-20 shrink-0 overflow-hidden rounded-md">
                        <ListingPhotoPlaceholder category={cartItem.item.category} size="xs" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate" data-testid={`cart-item-name-${cartItem.itemId}`}>
                        {stripDemoMarker(cartItem.item.name) || cartItem.item.name}
                      </h4>
                      {isWeightUnit ? (
                        <p className="text-sm text-muted-foreground" data-testid={`cart-item-weight-${cartItem.itemId}`}>
                          {unitDisplayLabel({ label: unitLabel, weightCentiLb: unitWeight })} · ${cartItem.lineSubtotal.toFixed(2)}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          ${price.toFixed(2)} each
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {!isWeightUnit && (
                          <div className="flex items-center border rounded-md">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => onUpdateQuantity(cartItem.itemId, cartItem.qty - 1)}
                              data-testid={`button-cart-decrease-${cartItem.itemId}`}
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="px-3 text-sm font-medium" data-testid={`text-cart-qty-${cartItem.itemId}`}>
                              {cartItem.qty}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => onUpdateQuantity(cartItem.itemId, cartItem.qty + 1)}
                              disabled={cartItem.qty >= cartItem.item.qtyAvailable}
                              data-testid={`button-cart-increase-${cartItem.itemId}`}
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemove(cartItem.itemId, (cartItem as any).weightUnitId)}
                          data-testid={`button-cart-remove-${cartItem.itemId}`}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold" data-testid={`text-cart-linetotal-${cartItem.itemId}`}>
                        ${cartItem.lineSubtotal.toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Totals */}
        {items.length > 0 && (
          <div className="border-t p-4 bg-card space-y-3" style={{ paddingBottom: "calc(16px + 60px + env(safe-area-inset-bottom, 0px))" }}>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span data-testid="text-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  {serviceFeeLabel(fees)}
                  <ServiceFeeInfo subtotal={subtotal} showUpsell />
                </span>
                <span data-testid="text-fee">${platformFee.toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span data-testid="text-total">${total.toFixed(2)}</span>
              </div>
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={onCheckout}
              data-testid="button-checkout"
            >
              Checkout
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-destructive"
              onClick={() => {
                if (window.confirm("Remove all items from your cart?")) onClearCart();
              }}
              data-testid="button-clear-cart"
            >
              Clear cart
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
